// src/screens/EditStats.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { LogoLoader } from "../components/LogoLoader";
import { Save, Plus, Minus } from "lucide-react";
import i18n from "../i18n";

/* ===== i18n mini-hook (comme les autres pages) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>) =>
    String((i18n.t as any)(key, options));
  return { t, lng };
}

type StatField =
  | "points"
  | "assists"
  | "rebounds"
  | "steals"
  | "blocks"
  | "turnovers"
  | "shots_attempted"
  | "shots_made"
  | "ft_attempted"
  | "ft_made"
  | "three_attempted"
  | "three_made"
  | "personal_fouls";

type PlayerStat = {
  id: string;
  match_id?: string;
  player_number: number;
  player_name: string;
  position: string;
  is_opponent?: boolean | null;
} & Record<StatField, number>;

const editableFields: StatField[] = [
  "points",
  "assists",
  "rebounds",
  "steals",
  "blocks",
  "turnovers",
  "shots_attempted",
  "shots_made",
  "ft_attempted",
  "ft_made",
  "three_attempted",
  "three_made",
  "personal_fouls",
];

export const EditStats = () => {
  const { t, lng } = useI18nSafe();

  const { id: matchId } = useParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState<PlayerStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      if (!matchId) return;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      const { data, error } = await supabase
        .from("match_stats")
        .select("*")
        .eq("match_id", matchId);

      if (error) {
        console.error("Erreur récupération stats :", error);
      } else {
        const rows = (data || []).map((row: any) => ({
          ...row,
          // sécurise les champs numériques potentiellement null
          points: row.points ?? 0,
          assists: row.assists ?? 0,
          rebounds: row.rebounds ?? 0,
          steals: row.steals ?? 0,
          blocks: row.blocks ?? 0,
          turnovers: row.turnovers ?? 0,
          shots_attempted: row.shots_attempted ?? 0,
          shots_made: row.shots_made ?? 0,
          ft_attempted: row.ft_attempted ?? 0,
          ft_made: row.ft_made ?? 0,
          three_attempted: row.three_attempted ?? 0,
          three_made: row.three_made ?? 0,
          personal_fouls: row.personal_fouls ?? 0,
        })) as PlayerStat[];

        setStats(rows);
      }

      setLoading(false);
    };

    fetchStats();
  }, [matchId, navigate]);

  // --- helpers update par ID (pour pouvoir séparer équipe / adversaires visuellement) ---
  const updateValue = (id: string, field: StatField, value: number) => {
    const next = Math.max(0, Number.isFinite(value) ? value : 0);
    setStats((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: next } : s))
    );
  };

  const nudge = (id: string, field: StatField, delta: number) => {
    setStats((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const current = Number.isFinite(s[field]) ? s[field] : 0;
        const next = Math.max(0, current + delta);
        return { ...s, [field]: next };
      })
    );
  };

  const handleSave = async () => {
    if (!stats.length) {
      alert(
        t("editstats:alerts.no_stats", {
          defaultValue: "Aucune stat à enregistrer.",
        })
      );
      return;
    }

    setSaving(true);
    try {
      for (const stat of stats) {
        const { error } = await supabase
          .from("match_stats")
          .update({
            points: stat.points,
            assists: stat.assists,
            rebounds: stat.rebounds,
            steals: stat.steals,
            blocks: stat.blocks,
            turnovers: stat.turnovers,
            shots_attempted: stat.shots_attempted,
            shots_made: stat.shots_made,
            ft_attempted: stat.ft_attempted,
            ft_made: stat.ft_made,
            three_attempted: stat.three_attempted,
            three_made: stat.three_made,
            personal_fouls: stat.personal_fouls,
          })
          .eq("id", stat.id);

        if (error) {
          console.error("Erreur update match_stats pour id =", stat.id, error);
          throw error;
        }
      }

      alert(t("editstats:alerts.save_ok"));
      if (matchId) {
        navigate(`/matchdetails/${matchId}`);
      }
    } catch (e: any) {
      console.error("Erreur globale lors de la sauvegarde des stats :", e);
      const extra = e?.message ? `\n\n${e.message}` : "";
      alert(t("editstats:alerts.save_err") + extra);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LogoLoader />;
  }

  // ===== Séparation équipe / adversaires =====
  const teamStats = stats.filter((s) => !s.is_opponent);
  const opponentStats = stats.filter((s) => s.is_opponent);

  const sortedTeamStats = [...teamStats].sort(
    (a, b) => a.player_number - b.player_number
  );
  const sortedOpponentStats = [...opponentStats].sort(
    (a, b) => a.player_number - b.player_number
  );

  const renderSection = (
    title: string,
    players: PlayerStat[],
    accentClass: string
  ) => {
    if (!players.length) return null;

    return (
      <div className="mb-8">
        <h2
          className={`text-lg font-bold mb-3 ${accentClass} flex items-center gap-2`}
        >
          {title}
        </h2>

        {players.map((s) => (
          <div
            key={s.id}
            className="mb-6 rounded-2xl border bg-white border-gray-200 shadow-sm p-4
                       dark:bg-gray-900 dark:border-gray-800"
          >
            <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
              #{s.player_number} — {s.player_name}{" "}
              <span className="text-sm text-gray-600 dark:text-gray-400">
                ({s.position || "-"})
              </span>
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {editableFields.map((field) => {
                const label = t(`editstats:statLabels.${field}`);
                return (
                  <div key={field} className="flex flex-col gap-1">
                    <span className="text-gray-700 dark:text-gray-300">
                      {label}
                    </span>
                    <div
                      className="flex items-stretch rounded-lg overflow-hidden border
                                 border-gray-300 dark:border-gray-700"
                    >
                      <button
                        type="button"
                        onClick={() => nudge(s.id, field, -1)}
                        className="px-2 flex items-center justify-center
                                   bg-gray-100 hover:bg-gray-200
                                   dark:bg-gray-800 dark:hover:bg-gray-700"
                        aria-label={t("editstats:aria.decrease", {
                          field: label,
                        })}
                      >
                        <Minus className="w-4 h-4" />
                      </button>

                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={s[field] ?? 0}
                        onChange={(e) =>
                          updateValue(
                            s.id,
                            field,
                            parseInt(e.target.value || "0", 10)
                          )
                        }
                        onWheel={(e) =>
                          (e.currentTarget as HTMLInputElement).blur()
                        }
                        className="w-full px-2 py-1 text-center outline-none
                                   bg-white dark:bg-gray-800
                                   text-gray-900 dark:text-gray-100"
                        aria-label={label}
                      />

                      <button
                        type="button"
                        onClick={() => nudge(s.id, field, +1)}
                        className="px-2 flex items-center justify-center
                                   bg-gray-100 hover:bg-gray-200
                                   dark:bg-gray-800 dark:hover:bg-gray-700"
                        aria-label={t("editstats:aria.increase", {
                          field: label,
                        })}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <h1 className="text-xl font-bold text-center mb-6 text-orange-600 dark:text-orange-400">
        ✏️ {t("editstats:title")}
      </h1>

      {sortedTeamStats.length === 0 && sortedOpponentStats.length === 0 && (
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
          {t("editstats:alerts.no_stats", {
            defaultValue: "Aucune statistique pour ce match.",
          })}
        </p>
      )}

      {/* Mon équipe */}
      {renderSection(
        lng === "fr" ? "Mon équipe" : "My team",
        sortedTeamStats,
        "text-emerald-600 dark:text-emerald-400"
      )}

      {/* Adversaires */}
      {renderSection(
        lng === "fr" ? "Adversaires" : "Opponents",
        sortedOpponentStats,
        "text-rose-600 dark:text-rose-400"
      )}

      <div className="text-center mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold
                      focus:outline-none focus:ring-2 focus:ring-orange-400/60
                      ${
                        saving
                          ? "bg-orange-300 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
        >
          <Save className="w-4 h-4" />
          {saving ? t("editstats:buttons.saving") : t("editstats:buttons.save")}
        </button>
      </div>
    </section>
  );
};

export default EditStats;
